import { useGetMetadataDetails } from '@modules/metadataManagement/hooks';
import { CheckCircleOutlineRounded, CloseRounded } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { IntegrationViewFragment } from 'generated/graphql';

interface SelectSourceMetadataFieldForCohortGroupProps {
  integration: IntegrationViewFragment;
  value: string;
  onChange: (value: string) => void;
}

export const SelectSourceMetadataFieldForCohortGroup = ({
  integration,
  value,
  onChange,
}: SelectSourceMetadataFieldForCohortGroupProps) => {
  const { metadataDetails, loading } = useGetMetadataDetails({
    namespacedSource: integration.source,
  });

  const integrationDisplayName = integration.namespace
    ? `${integration.channel.displayName} - ${integration.namespace}`
    : integration.channel.displayName;

  const metadataOptionsToDisplay = metadataDetails.filter(
    (metadata) => !metadata.isHidden,
  );

  const selectedMetadataField = metadataOptionsToDisplay.find(
    (metadata) => metadata.key === value,
  );

  const isDisabled = !!value && !selectedMetadataField;

  return (
    <Stack
      flexDirection="row"
      justifyContent="space-between"
      my={1}
      spacing={0.5}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          opacity: isDisabled ? 0.5 : 1,
        }}
      >
        <Box
          component="img"
          src={integration.channel.logo}
          width={30}
          height={30}
        />
        <Typography variant="body2">{integrationDisplayName}</Typography>
        {!!selectedMetadataField && (
          <CheckCircleOutlineRounded
            sx={{
              color: 'success.light',
            }}
          />
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box width={350}>
          <Autocomplete
            loading={loading}
            disabled={isDisabled}
            size="small"
            options={metadataOptionsToDisplay}
            getOptionLabel={(option) => option.displayText}
            value={selectedMetadataField || null}
            onChange={(_, newValue) => {
              onChange(newValue?.key || '');
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select metadata field" />
            )}
            isOptionEqualToValue={(option, value) => option.key === value.key}
            clearIcon={null}
          />
        </Box>
        <Box width={40}>
          {!!selectedMetadataField && (
            <IconButton size="small" onClick={() => onChange('')}>
              <CloseRounded />
            </IconButton>
          )}
        </Box>
      </Stack>
    </Stack>
  );
};
